import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    NavigationMenu,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    HomeIcon,
    FileIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "NavigationMenu";

const USAGE_CODE = `<NavigationMenu
    title="Main navigation"
>
    <NavigationMenu.Item
        title="Products"
        value="products"
    >
        <NavigationMenu.Content>
            <NavigationMenu.Group
                title="Design system"
            >
                <NavigationMenu.Link
                    subTitle="Buttons, inputs and layouts"
                    title="Components"
                    href="/components"
                    icon={FileIcon}
                />
            </NavigationMenu.Group>
            <NavigationMenu.Feature
                title="What's new"
                actionTitle="Read more"
                href="/release-notes"
            />
        </NavigationMenu.Content>
    </NavigationMenu.Item>
    <NavigationMenu.Item
        title="Pricing"
        href="/pricing"
        isActive={true}
    />
</NavigationMenu>`;

const PANEL_CODE = `<NavigationMenu
    panelBehaviour="fullWidth"
    title="Main navigation"
>
    ...
</NavigationMenu>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const PRODUCT_PAGES = [
    "components",
    "templates",
    "icons"
];

const createPageHref = (page: string) => `#nav-demo-${page}`;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("navigationMenuPage.contentTitle"),
        id: "content"
    },
    {
        title: t("navigationMenuPage.openingTitle"),
        id: "opening"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("navigationMenuPage.collapsedTitle"),
        id: "collapsed"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const NavigationMenuPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        controlHeights,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        activePage,
        setActivePage
    ] = useState("pricing");

    const [
        openValue,
        setOpenValue
    ] = useState<string | null>(null);

    const cellStyle: CSSProperties = {
        flexDirection: "column",
        gap: spaces.spacingSm,
        display: "flex"
    };

    const getBarStyle = (): CSSProperties => ({
        height: controlHeights.large + spaces.spacingSm * 2,
        paddingInline: spaces.spacingMd,
        boxSizing: "border-box",
        alignItems: "center",
        display: "flex",
        width: "100%"
    });

    const pageTitles: Record<string, string> = {
        components: t("navigationMenuPage.linkComponents"),
        templates: t("navigationMenuPage.linkTemplates"),
        icons: t("navigationMenuPage.linkIcons"),
        pricing: t("navigationMenuPage.pricing"),
        docs: t("navigationMenuPage.docs")
    };

    const handlePage = (page: string) => () => setActivePage(page);

    const handleValueChange = (nextValue: string | null) => setOpenValue(nextValue);

    const renderCell = (label: string, node: ReactNode) => <div
        style={cellStyle}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    const renderBar = (menu: ReactNode) => <DemoFrame>
        <div
            style={getBarStyle()}
        >
            {menu}
        </div>
    </DemoFrame>;

    const renderProducts = () => <NavigationMenu.Item
        isActive={PRODUCT_PAGES.includes(activePage)}
        title={t("navigationMenuPage.products")}
        value="products"
    >
        <NavigationMenu.Content>
            <NavigationMenu.Group
                title={t("navigationMenuPage.groupDesign")}
            >
                <NavigationMenu.Link
                    subTitle={t("navigationMenuPage.linkComponentsHint")}
                    title={t("navigationMenuPage.linkComponents")}
                    isActive={activePage === "components"}
                    href={createPageHref("components")}
                    onPress={handlePage("components")}
                    icon={FileIcon}
                />
                <NavigationMenu.Link
                    subTitle={t("navigationMenuPage.linkTemplatesHint")}
                    title={t("navigationMenuPage.linkTemplates")}
                    isActive={activePage === "templates"}
                    href={createPageHref("templates")}
                    onPress={handlePage("templates")}
                    icon={HomeIcon}
                />
                <NavigationMenu.Link
                    subTitle={t("navigationMenuPage.linkIconsHint")}
                    title={t("navigationMenuPage.linkIcons")}
                    isActive={activePage === "icons"}
                    href={createPageHref("icons")}
                    onPress={handlePage("icons")}
                    icon={StarIcon}
                />
            </NavigationMenu.Group>
        </NavigationMenu.Content>
    </NavigationMenu.Item>;

    const renderFeatureProducts = () => <NavigationMenu.Item
        title={t("navigationMenuPage.products")}
        value="products"
    >
        <NavigationMenu.Content
            size="large"
        >
            <NavigationMenu.Group
                title={t("navigationMenuPage.groupDesign")}
            >
                <NavigationMenu.Link
                    subTitle={t("navigationMenuPage.linkComponentsHint")}
                    title={t("navigationMenuPage.linkComponents")}
                    href={createPageHref("components")}
                    icon={FileIcon}
                />
                <NavigationMenu.Link
                    subTitle={t("navigationMenuPage.linkTemplatesHint")}
                    title={t("navigationMenuPage.linkTemplates")}
                    href={createPageHref("templates")}
                    icon={HomeIcon}
                />
            </NavigationMenu.Group>
            <NavigationMenu.Feature
                actionTitle={t("navigationMenuPage.featureAction")}
                subTitle={t("navigationMenuPage.featureHint")}
                title={t("navigationMenuPage.featureTitle")}
                href={createPageHref("whats-new")}
                icon={StarIcon}
            />
        </NavigationMenu.Content>
    </NavigationMenu.Item>;

    const renderResources = () => <NavigationMenu.Item
        title={t("navigationMenuPage.resources")}
        value="resources"
    >
        <NavigationMenu.Content>
            <NavigationMenu.Group
                title={t("navigationMenuPage.groupLearn")}
            >
                <NavigationMenu.Link
                    subTitle={t("navigationMenuPage.linkGuidesHint")}
                    title={t("navigationMenuPage.linkGuides")}
                    href={createPageHref("guides")}
                />
                <NavigationMenu.Link
                    subTitle={t("navigationMenuPage.linkBlogHint")}
                    title={t("navigationMenuPage.linkBlog")}
                    href={createPageHref("blog")}
                />
            </NavigationMenu.Group>
        </NavigationMenu.Content>
    </NavigationMenu.Item>;

    const renderPricing = () => <NavigationMenu.Item
        title={t("navigationMenuPage.pricing")}
        isActive={activePage === "pricing"}
        href={createPageHref("pricing")}
        onPress={handlePage("pricing")}
    />;

    const renderDocs = () => <NavigationMenu.Item
        title={t("navigationMenuPage.docs")}
        isActive={activePage === "docs"}
        href={createPageHref("docs")}
        onPress={handlePage("docs")}
    />;

    return <Article>
        <ComponentHeader
            lead={t("navigationMenuPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("navigationMenuPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("navigationMenuPage.overviewCaption", [
                    pageTitles[activePage] ?? activePage
                ])}
                isStacked={true}
            >
                {
                    renderBar(<NavigationMenu
                        title={t("navigationMenuPage.navTitle")}
                    >
                        {renderProducts()}
                        {renderDocs()}
                        {renderPricing()}
                    </NavigationMenu>)
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("navigationMenuPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("navigationMenuPage.contentText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderBar(<NavigationMenu
                        title={t("navigationMenuPage.navTitle")}
                    >
                        {renderFeatureProducts()}
                        {renderResources()}
                    </NavigationMenu>)
                }
            </Preview>
            <CodeBlock
                code={PANEL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("navigationMenuPage.openingTitle")}
            id="opening"
        >
            <Paragraph>
                {t("navigationMenuPage.openingText")}
            </Paragraph>
            <Preview
                caption={t("navigationMenuPage.openingCaption", [
                    JSON.stringify(openValue)
                ])}
                isStacked={true}
            >
                {
                    renderCell("openBehaviour=\"press\"", renderBar(<NavigationMenu
                        title={t("navigationMenuPage.navTitle")}
                        onValueChange={handleValueChange}
                        openBehaviour="press"
                        value={openValue}
                    >
                        {renderProducts()}
                        {renderResources()}
                        {renderPricing()}
                    </NavigationMenu>))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("navigationMenuPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => renderCell(size, renderBar(<NavigationMenu
                        title={`${t("navigationMenuPage.navTitle")} (${size})`}
                        size={size}
                    >
                        {renderResources()}
                        {renderDocs()}
                        {renderPricing()}
                    </NavigationMenu>)))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("navigationMenuPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderBar(<NavigationMenu
                        title={t("navigationMenuPage.navTitle")}
                    >
                        <NavigationMenu.Item
                            title={t("navigationMenuPage.resources")}
                            value="loading"
                        >
                            <NavigationMenu.Content
                                isLoading={true}
                            >
                                <NavigationMenu.Group>
                                    <NavigationMenu.Link
                                        title={t("navigationMenuPage.linkGuides")}
                                        href={createPageHref("guides")}
                                    />
                                </NavigationMenu.Group>
                            </NavigationMenu.Content>
                        </NavigationMenu.Item>
                        <NavigationMenu.Item
                            title={t("navigationMenuPage.docs")}
                            href={createPageHref("docs")}
                            isActive={true}
                        />
                        <NavigationMenu.Item
                            title={t("navigationMenuPage.archive")}
                            href={createPageHref("archive")}
                            isDisabled={true}
                        />
                    </NavigationMenu>)
                }
            </Preview>
        </Section>
        <Section
            title={t("navigationMenuPage.collapsedTitle")}
            id="collapsed"
        >
            <Paragraph>
                {t("navigationMenuPage.collapsedText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderBar(<NavigationMenu
                        footer={<Button
                            title={t("navigationMenuPage.signIn")}
                            onPress={() => undefined}
                            spreadBehaviour="stretch"
                        />}
                        title={t("navigationMenuPage.navTitle")}
                        collapseBehaviour="always"
                    >
                        {renderFeatureProducts()}
                        {renderResources()}
                        {renderPricing()}
                    </NavigationMenu>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("navigationMenuPage.a11yLandmark"),
                    t("navigationMenuPage.a11yTrigger"),
                    t("navigationMenuPage.a11yCurrent"),
                    t("navigationMenuPage.a11yBarKeys"),
                    t("navigationMenuPage.a11yPanelKeys"),
                    t("navigationMenuPage.a11yHover"),
                    t("navigationMenuPage.a11yLink"),
                    t("navigationMenuPage.a11yLoading"),
                    t("navigationMenuPage.a11yCollapsed"),
                    t("navigationMenuPage.a11yMotion"),
                    t("navigationMenuPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("navigationMenuPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("navigationMenuPage.propOpenBehaviour"),
                        type: "\"hover\" | \"press\"",
                        defaultValue: "\"hover\"",
                        name: "openBehaviour"
                    },
                    {
                        description: t("navigationMenuPage.propPanelBehaviour"),
                        type: "\"floating\" | \"fullWidth\"",
                        defaultValue: "\"floating\"",
                        name: "panelBehaviour"
                    },
                    {
                        description: t("navigationMenuPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("navigationMenuPage.propValue"),
                        type: "string | null",
                        name: "value"
                    },
                    {
                        description: t("navigationMenuPage.propOnValueChange"),
                        type: "(value: string | null) => void",
                        name: "onValueChange"
                    },
                    {
                        description: t("navigationMenuPage.propCollapseBehaviour"),
                        type: "\"auto\" | \"always\" | \"never\"",
                        defaultValue: "\"auto\"",
                        name: "collapseBehaviour"
                    },
                    {
                        description: t("navigationMenuPage.propFooter"),
                        type: "ReactNode",
                        name: "footer"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("navigationMenuPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("navigationMenuPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("navigationMenuPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    NavigationMenu.Item
                </InlineCode>
                {" "}
                {t("navigationMenuPage.partItemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("navigationMenuPage.propItemTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("navigationMenuPage.propItemHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("navigationMenuPage.propItemValue"),
                        name: "value",
                        type: "string"
                    },
                    {
                        description: t("navigationMenuPage.propItemIsActive"),
                        defaultValue: "false",
                        name: "isActive",
                        type: "boolean"
                    },
                    {
                        description: t("navigationMenuPage.propItemIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("navigationMenuPage.propItemOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("navigationMenuPage.itemNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    NavigationMenu.Content
                </InlineCode>
                {" "}
                {t("navigationMenuPage.partContentText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("navigationMenuPage.propContentSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("navigationMenuPage.propContentIsLoading"),
                        name: "isLoading",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("navigationMenuPage.contentNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    NavigationMenu.Group
                </InlineCode>
                {" "}
                {t("navigationMenuPage.partGroupText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("navigationMenuPage.propGroupTitle"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("navigationMenuPage.groupNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    NavigationMenu.Link
                </InlineCode>
                {" "}
                {t("navigationMenuPage.partLinkText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("navigationMenuPage.propLinkTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("navigationMenuPage.propLinkHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("navigationMenuPage.propLinkSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("navigationMenuPage.propLinkIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("navigationMenuPage.propLinkIsActive"),
                        defaultValue: "false",
                        name: "isActive",
                        type: "boolean"
                    },
                    {
                        description: t("navigationMenuPage.propLinkIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("navigationMenuPage.propLinkOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("navigationMenuPage.linkNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    NavigationMenu.Feature
                </InlineCode>
                {" "}
                {t("navigationMenuPage.partFeatureText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("navigationMenuPage.propFeatureTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("navigationMenuPage.propFeatureHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("navigationMenuPage.propFeatureSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("navigationMenuPage.propFeatureActionTitle"),
                        name: "actionTitle",
                        type: "string"
                    },
                    {
                        description: t("navigationMenuPage.propFeatureIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("navigationMenuPage.propFeatureOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("navigationMenuPage.featureNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default NavigationMenuPage;
